import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type Column } from '@/components/shared/DataTable';
import { useCurrency } from '@/hooks/useCurrency';
import { useVisibleProductCatFilters } from '@/hooks/useVisibleProductCatFilters';
import type { SafetyStockSummaryRow } from '@/services/safetyStockService';
import {
  FIXED_SUMMARY_DIMENSIONS,
  catFieldToDimension,
  formatDays,
  formatPercentFraction,
  formatSafetyStockMethod,
  formatUnits,
  shareOfTotal,
  toNumber,
} from '../utils/format';

interface Props {
  dimension: string;
  onDimensionChange: (dimension: string) => void;
  rows: SafetyStockSummaryRow[];
  totalValue: string | number | null | undefined;
  loading?: boolean;
  disabled?: boolean;
}

export function BreakdownSection({
  dimension,
  onDimensionChange,
  rows,
  totalValue,
  loading,
  disabled,
}: Props) {
  const { t, i18n } = useTranslation();
  const { formatRounded, symbol } = useCurrency();
  const locale = i18n.language || 'es';
  const visibleCats = useVisibleProductCatFilters();

  const dimensionOptions = useMemo(() => {
    const fixed = FIXED_SUMMARY_DIMENSIONS.map((d) => ({
      value: d,
      label: t(`safetyStock.summary.dimensions.${d}`),
    }));
    const cats = visibleCats.map((field) => ({
      value: catFieldToDimension(field),
      label: t(`productCatFilters.${field}`),
    }));
    return [...fixed, ...cats];
  }, [t, visibleCats]);

  const columns: Column[] = useMemo(
    () => [
      {
        header: t('safetyStock.summary.breakdown.segment'),
        accessor: 'dimension_label',
        cell: (_v, row: SafetyStockSummaryRow) => {
          const raw = row.dimension_value || row.dimension_label || '';
          if (dimension === 'METHOD') return formatSafetyStockMethod(raw, t);
          return row.dimension_label || row.dimension_value || '—';
        },
        exportHeader: t('safetyStock.summary.breakdown.segment'),
        exportValue: (_v, row: SafetyStockSummaryRow) => {
          const raw = row.dimension_value || row.dimension_label || '';
          if (dimension === 'METHOD') return formatSafetyStockMethod(raw, t);
          return row.dimension_label || row.dimension_value || '';
        },
      },
      {
        header: t('safetyStock.summary.breakdown.skus'),
        accessor: 'items',
        numeric: true,
        align: 'right',
        exportType: 'integer',
      },
      {
        header: t('safetyStock.summary.breakdown.units'),
        accessor: 'safety_stock_units',
        numeric: true,
        align: 'right',
        cell: (v) => formatUnits(v, locale),
        exportType: 'number',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.summary.breakdown.value', { currencySymbol: symbol }),
        accessor: 'safety_stock_value',
        numeric: true,
        align: 'right',
        cell: (v) => (toNumber(v) == null ? '—' : formatRounded(v)),
        exportType: 'currency',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.summary.breakdown.share', { currencySymbol: symbol }),
        accessor: 'share',
        numeric: true,
        align: 'right',
        cell: (_v, row: SafetyStockSummaryRow) =>
          formatPercentFraction(shareOfTotal(row.safety_stock_value, totalValue), locale),
        exportType: 'number',
        exportValue: (_v, row: SafetyStockSummaryRow) =>
          shareOfTotal(row.safety_stock_value, totalValue),
      },
      {
        header: t('safetyStock.summary.breakdown.pctProtection'),
        accessor: 'ss_pct_protection_value',
        numeric: true,
        align: 'right',
        cell: (_v, row: SafetyStockSummaryRow) => {
          const pct = row.ss_pct_protection_value ?? row.ss_pct_protection_units;
          if (
            row.no_data_reasons?.ss_pct_protection_value ||
            row.no_data_reasons?.ss_pct_protection_units
          ) {
            return '—';
          }
          return formatPercentFraction(pct, locale);
        },
        exportType: 'number',
        exportValue: (_v, row: SafetyStockSummaryRow) =>
          toNumber(row.ss_pct_protection_value ?? row.ss_pct_protection_units),
      },
      {
        header: t('safetyStock.summary.breakdown.coverageDays'),
        accessor: 'coverage_days',
        numeric: true,
        align: 'right',
        cell: (_v, row: SafetyStockSummaryRow) =>
          formatDays(
            row.coverage_days_median ?? row.coverage_days_units ?? row.coverage_days_value,
            locale,
          ),
        exportType: 'number',
        exportValue: (_v, row: SafetyStockSummaryRow) =>
          toNumber(
            row.coverage_days_median ?? row.coverage_days_units ?? row.coverage_days_value,
          ),
      },
      {
        header: t('safetyStock.summary.breakdown.avgProtection'),
        accessor: 'avg_protection_days',
        numeric: true,
        align: 'right',
        cell: (v) => formatDays(v, locale),
        exportType: 'number',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.summary.breakdown.avgCsl'),
        accessor: 'avg_service_level',
        numeric: true,
        align: 'right',
        cell: (v) => formatPercentFraction(v, locale),
        exportType: 'number',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.summary.breakdown.deltaValue', { currencySymbol: symbol }),
        accessor: 'delta_value',
        numeric: true,
        align: 'right',
        cell: (v) => (toNumber(v) == null ? '—' : formatRounded(v)),
        exportType: 'currency',
        exportValue: (v) => toNumber(v),
      },
    ],
    [t, locale, formatRounded, symbol, totalValue, dimension],
  );

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('safetyStock.summary.breakdown.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('safetyStock.summary.breakdown.help')}
          </p>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700">
            {t('safetyStock.summary.breakdown.dimension')}
          </span>
          <select
            className="min-w-[12rem] rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary disabled:bg-gray-50"
            value={dimension}
            disabled={disabled}
            onChange={(e) => onDimensionChange(e.target.value)}
          >
            {dimensionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        allowExport
        exportFilename={`safety-stock-breakdown-${dimension.toLowerCase()}`}
        pagination
        compact
      />
    </section>
  );
}
